// POM-6600 — what the Add Inventory Item review says. Every sentence states a fact about the
// catalog or inventory; none of them says two products are interchangeable.

import { formatForm, formatPackSize } from 'modules/CatalogManagement/utils/format';

export const LINK_CHECK_STATUS = {
  LOADING: 'loading',
  LOADED: 'loaded',
  FAILED: 'failed',
};

// Refusals createInventory would give too, so Confirm could never succeed; the dialog shows them on
// the form. Any other failure (network, a core without the query yet) still opens the review step,
// so stock intake is never blocked.
const LINK_CHECK_REFUSAL_CODES = [
  'CATALOG_MISSING_PRODUCT_ID',
  'CATALOG_NOT_FOUND',
  'INPUT_VALIDATION_ERROR',
];

export const isLinkCheckRefusal = (error) =>
  LINK_CHECK_REFUSAL_CODES.includes(error?.response?.errors?.[0]?.extensions?.code);

const normalizeForComparison = (value) => (value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

const isSameText = (first, second) =>
  normalizeForComparison(first) === normalizeForComparison(second);

export const compareAlphabetically = (first, second) =>
  first.localeCompare(second, undefined, { sensitivity: 'base' });

export const countLabel = (count, singular, plural) =>
  `${count} ${count === 1 ? singular : plural}`;

// Split exactly as core splits it for the similar-products search.
export const firstWordOf = (drugName) => (drugName ?? '').trim().split(/\s+/)[0];

export const productNameOf = (product) =>
  product.displayName || [product.drugName, product.strength].filter(Boolean).join(' ');

export const listStrengthFormPack = (product) => [
  { label: 'Strength', value: product.strength || '-' },
  { label: 'Form', value: formatForm(product.formAlias, product.dosageForm) },
  { label: 'Pack size', value: formatPackSize(product.packSizeValue, product.packSizeUnit) },
];

// The banner under the form when the entered NDC is already in inventory. It names the product
// that holds it, because an NDC on another product than the pick can itself be an old wrong link.
export const describeExistingItem = ({ existingItem, pickedProductId }) => {
  const itemName = [existingItem.drugName, existingItem.strength].filter(Boolean).join(' ');
  const holder = [existingItem.productId, existingItem.manufacturer].filter(Boolean).join(' · ');
  return [
    `${existingItem.ndc} is already in inventory as ${itemName || 'an item'} (${holder}).`,
    ...(existingItem.productId === pickedProductId
      ? []
      : ['That is a different product from the one you picked.']),
    'An NDC can be added only once. Check the NDC on the label, or receive stock against the existing item.',
  ].join(' ');
};

// Mirrors createInventory: a product with no default NDC takes the new one as its default, and an
// existing default is replaced only when "Set as the default NDC" is ticked.
export const describeDefaultNdcEffect = ({ currentDefaultNdc, isSetAsDefault }) => {
  if (!currentDefaultNdc) {
    return {
      answer: 'Yes',
      effect: 'The product has no default NDC yet, so this one becomes it.',
      isReplacing: false,
    };
  }
  if (isSetAsDefault) {
    return {
      answer: 'Yes',
      effect: `Replaces ${currentDefaultNdc} as the default.`,
      isReplacing: true,
    };
  }
  return { answer: 'No', effect: `${currentDefaultNdc} stays the default.`, isReplacing: false };
};

// Deliberately two groups rather than one alphabetical list: the wrong links found in prod were
// all to a product with a different name (IR vs SR / ER), so that group is read first. Each group is
// alphabetical.
export const groupSimilarProducts = (similarProducts, pickedDrugName) => {
  const compareProductNames = (first, second) =>
    compareAlphabetically(productNameOf(first), productNameOf(second));
  const hasPickedDrugName = (product) => isSameText(product.drugName, pickedDrugName);

  return {
    differentName: similarProducts
      .filter((product) => !hasPickedDrugName(product))
      .sort(compareProductNames),
    sameDrugName: similarProducts.filter(hasPickedDrugName).sort(compareProductNames),
  };
};

export const filterProductsBySearchText = (products, searchText) => {
  const normalizedSearchText = normalizeForComparison(searchText);
  if (!normalizedSearchText) return products;
  return products.filter((product) =>
    [product.displayName, product.drugName, product.productId].some((fieldValue) =>
      normalizeForComparison(fieldValue).includes(normalizedSearchText)
    )
  );
};
